import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getPath } from "@/lib/academy.functions";
import { ArrowRight, Award, Check, Circle, CircleDashed, Clock, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ACADEMY_ID, NOINDEX_META, breadcrumbs, courseNode, ldScript, seoMeta } from "@/lib/schema";
import { absoluteUrl } from "@/lib/site";

type ProgressRow = { lesson_id: string; status: string; progress_pct: number | null; mastered_at: string | null };

export const Route = createFileRoute("/academy/$domain/$path")({
  loader: async ({ params }) => {
    const data = await getPath({ data: { domainSlug: params.domain, pathSlug: params.path } });
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Path unavailable — Buddha KA Academy" }, NOINDEX_META] };
    }
    const p = loaderData.path;
    const path = `/academy/${params.domain}/${params.path}`;
    const title = `${p.title} — Buddha KA Academy`;
    const description =
      p.description ?? p.subtitle ?? `${p.title}: a guided learning path inside the Buddha KA Academy.`;
    return {
      ...seoMeta({ title, description, path }),
      scripts: [
        ldScript(
          courseNode({
            id: `${absoluteUrl(path)}#course`,
            url: absoluteUrl(path),
            name: p.title,
            description,
            partOfCourseId: `${absoluteUrl(`/academy/${params.domain}`)}#course`,
            educationalLevel: p.difficulty ?? undefined,
            lessons: loaderData.lessons.map((l) => ({
              path: `/academy/${params.domain}/lesson/${l.slug}`,
              name: l.title,
            })),
            extra: { isPartOf: [{ "@id": `${absoluteUrl(`/academy/${params.domain}`)}#course` }, { "@id": ACADEMY_ID }] },
          }),
        ),
        ldScript(
          breadcrumbs([
            ["Academy", "/academy"],
            [params.domain, `/academy/${params.domain}`],
            [p.title, path],
          ]),
        ),
      ],
    };
  },

  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">Path not found</h1>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="text-sm text-foreground/60">{(error as Error).message}</p>
    </div>
  ),
  component: PathPage,
});

function useLessonProgress(lessonIds: string[]) {
  const [progress, setProgress] = useState<Record<string, ProgressRow>>({});
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const key = lessonIds.join(",");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      setSignedIn(!!user);
      if (!user || lessonIds.length === 0) { setProgress({}); return; }
      const { data } = await supabase
        .from("academy_progress")
        .select("lesson_id, status, progress_pct, mastered_at")
        .eq("user_id", user.id)
        .in("lesson_id", lessonIds);
      if (cancelled) return;
      const map: Record<string, ProgressRow> = {};
      (data ?? []).forEach((r: any) => { map[r.lesson_id] = r as ProgressRow; });
      setProgress(map);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { progress, signedIn };
}

function PathPage() {
  const data = Route.useLoaderData();
  const { domain } = Route.useParams();
  if (!data) return null;
  const { path, lessons } = data;
  const lessonIds = (lessons as any[]).map((l) => l.id);
  const { progress, signedIn } = useLessonProgress(lessonIds);

  const completedCount = Object.values(progress).filter((p) => p.status === "completed").length;
  const inProgressCount = Object.values(progress).filter((p) => p.status !== "completed" && (p.progress_pct ?? 0) > 0).length;
  const masteredCount = Object.values(progress).filter((p) => !!p.mastered_at).length;
  const pct = lessons.length ? Math.round((completedCount / lessons.length) * 100) : 0;

  return (
    <div className="min-h-dvh bg-obsidian text-foreground">
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <Link to="/academy/$domain" params={{ domain }} className="text-xs uppercase tracking-[0.3em] text-gold">
          ← Back
        </Link>
        <h1 className="mt-4 font-serif text-4xl text-gold-luminous">{path.title}</h1>
        {path.subtitle && <p className="mt-1 text-sm uppercase tracking-widest text-foreground/60">{path.subtitle}</p>}
        {path.description && <p className="mt-4 max-w-2xl text-foreground/75">{path.description}</p>}

        {signedIn && lessons.length > 0 && (
          <div className="mt-6 rounded-xl border hairline p-4" aria-live="polite">
            <div className="flex items-center justify-between text-xs uppercase tracking-widest text-foreground/60">
              <span>Your progress</span>
              <span className="text-gold">{completedCount} / {lessons.length} lessons · {pct}%</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-foreground/10">
              <div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} aria-hidden />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-foreground/60">
              {inProgressCount > 0 && <span>{inProgressCount} in progress</span>}
              <span className="inline-flex items-center gap-1 text-gold/80">
                <Award size={11} /> {masteredCount} ready · scenarios mastered
              </span>
            </div>
          </div>
        )}
        {signedIn === false && lessons.length > 0 && (
          <p className="mt-4 text-xs text-foreground/50">
            <Link to="/auth" className="underline decoration-gold/40 underline-offset-4 hover:text-gold">Sign in</Link> to track completion for each lesson.
          </p>
        )}

        <ol className="mt-8 space-y-3">
          {(lessons as any[]).map((l, i) => {
            const p = progress[l.id];
            const isDone = p?.status === "completed";
            const isStarted = !isDone && (p?.progress_pct ?? 0) > 0;
            const isReady = !!p?.mastered_at;
            const statusLabel = isDone ? "Completed" : isStarted ? "In progress" : "Not started";
            const readyLabel = isReady ? " · Ready" : "";
            return (
              <li key={l.id}>
                <Link to="/academy/$domain/lesson/$lesson" params={{ domain, lesson: l.slug }}
                  aria-label={`${l.title} — ${statusLabel}${readyLabel}`}
                  className={`group flex items-start justify-between gap-3 rounded-xl border p-4 transition hover:border-gold/60 ${isDone ? "border-gold/40 bg-gold/[0.03]" : "hairline"}`}>
                  <div className="flex min-w-0 items-start gap-3">
                    <span
                      className={`mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full border text-xs ${
                        isDone ? "border-gold bg-gold/15 text-gold" : isStarted ? "border-gold/60 text-gold" : "hairline text-foreground/70"
                      }`}
                      aria-hidden
                    >
                      {isDone ? <Check size={14} /> : isStarted ? <CircleDashed size={14} /> : (i + 1)}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-serif text-lg">{l.title}</h3>
                        {l.tier === "premium" && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-gold/50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold">
                            <Lock size={10} /> Premium
                          </span>
                        )}
                        {signedIn && (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                              isDone ? "bg-gold/15 text-gold" : isStarted ? "border border-gold/40 text-gold/80" : "border hairline text-foreground/50"
                            }`}
                          >
                            {isDone ? <Check size={10} /> : isStarted ? <CircleDashed size={10} /> : <Circle size={10} />}
                            {statusLabel}
                          </span>
                        )}
                        {signedIn && isReady && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-gold/60 bg-gold/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold">
                            <Award size={10} /> Ready
                          </span>
                        )}
                      </div>
                      {l.summary && <p className="mt-1 line-clamp-2 text-sm text-foreground/70">{l.summary}</p>}
                      <p className="mt-1 flex items-center gap-1 text-xs text-foreground/50">
                        <Clock size={12} /> {l.est_minutes} min · {l.difficulty}
                      </p>
                    </div>
                  </div>
                  <ArrowRight size={18} className="mt-1 shrink-0 text-gold group-hover:translate-x-1 transition-transform" />
                </Link>
              </li>
            );
          })}
          {lessons.length === 0 && (
            <li className="rounded-xl border hairline p-6 text-center text-sm text-foreground/60">
              Lessons for this path are being prepared.
            </li>
          )}
        </ol>
      </section>
    </div>
  );
}
